<template>
    <div>
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && homepage.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card mb-2">
                    <div class="card-body">
                        <label>
                            Campus
                        </label>
                        <campus-selector
                            v-model="homepage.campusId"
                            clearable
                        />
                        <div v-if="hasError('campusId')" class="error-box my-1">
                            {{ getError('campusId') }}
                        </div>
                    </div>
                </div>

                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="homepage.value"
                            :edit-mode="editMode"
                            :validation="$v.homepage.value"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="homepage"
                            @meta-change="onMetaChange"
                            @on-delete-confirm="onDeleteConfirm"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { StatusCodes as HTTP } from 'http-status-codes';
import CampusSelector from '@/components/campuses/Selector';
import AddEditForm from '@/components/homepages/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Homepage'
    },

    validations: {
        homepage: {
            campusId: {
                required
            },
            value: {
                newsbreaker: {
                    news: {
                        $each: {
                            text: {
                                required
                            },
                            link: {
                                required
                            }
                        }
                    }
                },
                tiles: {
                    $each: {
                        image: { required },
                        title: { required },
                        redirect: { required },
                        premiumOnly: { required },
                        premiumOnlyImage: {
                            requiredIf: (v, { premiumOnly }) =>
                                !!(!premiumOnly || (premiumOnly && v && v.id))
                        }
                    }
                },
                slides: {
                    $each: {
                        upperText: { required },
                        lowerText: { required },
                        buttonText: { required },
                        buttonLink: { required },
                        image: { required }
                    }
                }
            }
        }
    },

    components: {
        AddEditForm,
        CampusSelector,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit homepage' : 'Add homepage',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Homepages',
                    to: '/homepages'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            homepage: {
                value: {
                    newsbreaker: {
                        news: []
                    },
                    slides: [],
                    tiles: []
                },
                meta: {}
            },
            serverErrors: []
        };
    },

    computed: {
        sections() {
            const sections = {
                meta: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.homepage = await this.getHomepage(this.id);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addHomepage: 'homepages/store',
            deleteHomepage: 'homepages/destroy',
            getHomepage: 'homepages/show',
            updateHomepage: 'homepages/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                if (!this.editMode) {
                    const added = await this.addHomepage(this.homepage);

                    this.$router.push(`/homepages/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateHomepage({
                        id: this.id,
                        data: this.homepage
                    });

                    this.homepage.updatedAt = updatedAt;

                    this.$toaster('Homepage updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteHomepage(this.id);

                this.$router.push('/homepages');
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        onMetaChange(meta) {
            this.homepage.meta = { ...meta };
        },

        hasError(key) {
            return (
                this.$v.homepage[key].$error ||
                this.serverErrors.find(error => error.param === key)
            );
        },

        getError(key) {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.homepage,
                this.serverErrors,
                key
            );
        }
    }
};
</script>
